import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { PrincipalFormData, PrincipalFormErrors, PrincipalBank } from "../types";
import { useLobRegion } from "../../../../hooks/useLobRegion";

interface BankDetailsFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onAddBankAccount: () => void;
  onRemoveBankAccount: (id: string) => void;
  onBankAccountChange: (id: string, field: keyof PrincipalBank, value: string) => void;
  isEditMode?: boolean;
}

const BankDetailsForm: React.FC<BankDetailsFormProps> = ({
  formData,
  formErrors,
  onAddBankAccount,
  onRemoveBankAccount,
  onBankAccountChange,
  isEditMode = false,
}) => {
  const region = useLobRegion();
  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Bank Details (Optional)
        </h2>
        <button
          type="button"
          onClick={onAddBankAccount}
          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Bank Account
        </button>
      </div>

      <div className="space-y-6">
        {formData.principalBanks.length > 0 ? (
          formData.principalBanks.map((account, index) => (
            <div key={account.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-medium text-gray-900">
                  Bank Account {index + 1}
                  {account.principalBankId && (
                    <span className="ml-2 text-xs text-gray-500">(Existing)</span>
                  )}
                </h3>
                {!account.principalBankId && (
                  <button
                    type="button"
                    onClick={() => onRemoveBankAccount(account.id)}
                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    title="Remove Bank Account"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {account.principalBankId && (
                  <span className="text-xs text-gray-500 italic">Cannot be deleted</span>
                )}
              </div>

              <div className="space-y-4">
                {/* Bank Name and Account Holder Name Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={account.name}
                      onChange={(e) => onBankAccountChange(account.id, "name", e.target.value)}
                      placeholder="Enter Bank Name"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={account.accountHolderName}
                      onChange={(e) => onBankAccountChange(account.id, "accountHolderName", e.target.value)}
                      placeholder="Enter Account Holder Name"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>

                {/* Account Number and IFSC Code Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={account.accountNo}
                      onChange={(e) => onBankAccountChange(account.id, "accountNo", e.target.value)}
                      placeholder="Enter Account Number"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    />
                  </div>

                  {region.indiaFields && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      value={account.ifscCode}
                      onChange={(e) => onBankAccountChange(account.id, "ifscCode", e.target.value)}
                      placeholder="Enter IFSC Code"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    />
                  </div>
                  )}
                </div>

                {/* Bank City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank City
                  </label>
                  <input
                    type="text"
                    value={account.bankCity}
                    onChange={(e) => onBankAccountChange(account.id, "bankCity", e.target.value)}
                    placeholder="Enter Bank City"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                  />
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            No bank accounts added. Click "Add Bank Account" to add bank details.
          </p>
        )}

        {formErrors.principalBanks && (
          <div className="text-red-500 text-sm">
            {formErrors.principalBanks}
          </div>
        )}
      </div>
    </div>
  );
};

export default BankDetailsForm;

